


import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Container, Typography, Card, Paper, Grid, Tabs, Tab,
  IconButton, Button, CircularProgress, Divider, Alert
} from '@mui/material';
import {
  ContentCopy as CopyIcon, Share as ShareIcon, CardGiftcard as GiftIcon,
  AccountBalanceWallet as WalletIcon, ReceiptLong as ActivityIcon, ArrowBack as ArrowBackIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';

import { useNavigate } from "react-router-dom";

import { apiRequest } from '../../../fetchSetup';
import CustomDialog from '../../../CustomDialog';

const REFERRAL_API = process.env.REACT_APP_REFERRAL_API;

const formatDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) +
    ', ' + d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const formatDateOnly = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const diffMs = new Date(iso).getTime() - Date.now();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'earned', label: 'Earned' },
  { key: 'reversed', label: 'Reversed' },
  { key: 'expired', label: 'Expired' },
];

const ActivityRow = ({ item }) => {
  const isCredit = item.type === 'credit';
  const isActiveCredit = isCredit && (item.txn_status === 'active' || item.txn_status === 'partially_reversed');
  const remaining = isActiveCredit ? daysUntil(item.expiry_date) : null;
  const expiringSoon = remaining !== null && remaining <= 2 && remaining >= 0;

  return (
    <Box sx={{
      py: 1.75, borderBottom: '1px solid #F0F0F0',
      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'
    }}>
      <Box sx={{ minWidth: 0, pr: 2 }}>
        <Typography variant="body2" fontWeight="600" noWrap>
          {item.title}
        </Typography>
        {item.subtitle && (
          <Typography variant="caption" color="text.secondary" display="block">
            {item.subtitle}
          </Typography>
        )}
        <Typography variant="caption" sx={{ color: '#B0B0B0' }} display="block">
          {formatDate(item.date)}{item.order_id ? ` • #${item.order_id}` : ''}
        </Typography>
        {isActiveCredit && item.expiry_date && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <ScheduleIcon sx={{ fontSize: 13, color: expiringSoon ? '#B45309' : 'text.secondary' }} />
            <Typography
              variant="caption"
              sx={{ color: expiringSoon ? '#B45309' : 'text.secondary', fontWeight: expiringSoon ? 700 : 400 }}
            >
              Expires {formatDateOnly(item.expiry_date)}
              {remaining !== null && ` (${remaining === 0 ? 'today' : `${remaining}d left`})`}
            </Typography>
          </Box>
        )}
      </Box>
      <Typography
        variant="body2"
        fontWeight="700"
        sx={{ color: isCredit ? '#059669' : '#B91C1C', whiteSpace: 'nowrap', pt: 0.25 }}
      >
        {isCredit ? '+' : '-'}₹{item.amount.toFixed(2)}
      </Typography>
    </Box>
  );
};

const ReferralPage = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [dialog, setDialog] = useState({ open: false, message: '', type: 'success', title: '' });
  const navigate = useNavigate();

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const response = await apiRequest(`${REFERRAL_API}/referral/summary`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to load referral data');
      const data = await response.json();
      setSummary(data);
    } catch (err) {
      setDialog({ open: true, message: err.message, type: 'error', title: 'Error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = () => {
    if (!summary?.referral_code) return;
    navigator.clipboard.writeText(summary.referral_code);
    setDialog({ open: true, message: 'Referral code copied!', type: 'success', title: 'Copied' });
  };

  const handleShare = async () => {
    const shareText = `Use my referral code "${summary?.referral_code}" to sign up on Ayuja and get a welcome bonus! 🎁`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Join Ayuja', text: shareText });
      } catch (e) { /* user cancelled share, ignore */ }
    } else {
      navigator.clipboard.writeText(shareText);
      setDialog({ open: true, message: 'Referral message copied to clipboard!', type: 'success', title: 'Copied' });
    }
  };

  const filteredActivity = useMemo(() => {
    const activity = summary?.wallet_activity || [];
    if (activeTab === 'all') return activity;
    return activity.filter((a) => a.category === activeTab);
  }, [summary, activeTab]);

  const expiringSoon = summary?.expiring_soon;
  const showExpiryBanner = expiringSoon && expiringSoon.count > 0 && expiringSoon.amount > 0;

  if (loading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: '#006D77' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <IconButton onClick={() => navigate(-1)}
            sx={{ color: "#006D77", border: "1px solid #E5E7EB", bgcolor: "#fff", "&:hover": { bgcolor: "#F3F4F6" } }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight="bold" color="#006D77" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <GiftIcon sx={{ fontSize: 40 }} />
            Refer & Earn
          </Typography>
        </Box>

        {showExpiryBanner && (
          <Alert
            severity="warning"
            icon={<ScheduleIcon />}
            sx={{
              mb: 3, borderRadius: 2, bgcolor: '#FFF7ED', color: '#92400E',
              border: '1px solid #FDE68A', '& .MuiAlert-icon': { color: '#B45309' },
            }}
          >
            <strong>₹{expiringSoon.amount.toFixed(2)}</strong> in referral points
            {expiringSoon.count > 1 ? ` (across ${expiringSoon.count} rewards)` : ''} will expire within the next {expiringSoon.within_days} days — use your wallet balance before it lapses.
          </Alert>
        )}

        {/* Referral Code + Wallet Summary */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={7}>
            <Card sx={{ p: 3, background: 'linear-gradient(135deg, #006D77 0%, #005560 100%)', color: 'white', borderRadius: 3 }}>
              <Typography variant="subtitle2" sx={{ opacity: 0.85, mb: 1 }}>
                Your Referral Code
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Typography variant="h3" fontWeight="bold" sx={{ letterSpacing: 2 }}>
                  {summary?.referral_code || '—'}
                </Typography>
                <IconButton onClick={handleCopyCode} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)' }}>
                  <CopyIcon />
                </IconButton>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
                Share this code — you earn ₹100 when your friend places their first order, and they get a ₹50 welcome bonus.
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.75, display: 'block', mb: 2 }}>
                Referral points expire within 30 days of being credited, and may be reversed if the qualifying order is returned.
              </Typography>
              <Button
                variant="contained"
                startIcon={<ShareIcon />}
                onClick={handleShare}
                sx={{ bgcolor: 'white', color: '#006D77', textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: '#F0F9FF' } }}
              >
                Share Code
              </Button>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card sx={{ p: 3, borderRadius: 3, height: '80%', border: '1px solid #E0E0E0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <WalletIcon sx={{ color: '#006D77' }} />
                <Typography variant="subtitle2" color="text.secondary">Wallet Balance</Typography>
              </Box>
              <Typography variant="h3" fontWeight="bold" color="#006D77" sx={{ mb: 2 }}>
                ₹{(summary?.wallet_balance || 0).toFixed(2)}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Total Referrals</Typography>
                  <Typography variant="h6" fontWeight="bold">{summary?.total_referrals || 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Earned from Referrals</Typography>
                  <Typography variant="h6" fontWeight="bold" color="#059669">
                    ₹{(summary?.total_earned_from_referrals || 0).toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>

        {/* Wallet Activity — tabbed transaction ledger */}
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <ActivityIcon sx={{ color: '#006D77' }} />
            <Typography variant="h6" fontWeight="bold">Wallet Activity</Typography>
          </Box>

          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{
              mb: 1,
              minHeight: 36,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 36, fontSize: 14, color: 'text.secondary' },
              '& .Mui-selected': { color: '#006D77 !important' },
              '& .MuiTabs-indicator': { bgcolor: '#006D77' },
            }}
          >
            {TABS.map((t) => <Tab key={t.key} value={t.key} label={t.label} />)}
          </Tabs>

          {filteredActivity.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography color="text.secondary">
                {activeTab === 'all' ? 'No activity yet.' : `No ${activeTab} activity yet.`}
              </Typography>
            </Box>
          ) : (
            <Box>
              {filteredActivity.map((item, idx) => <ActivityRow key={idx} item={item} />)}
            </Box>
          )}
        </Paper>
      </Container>

      <CustomDialog
        open={dialog.open}
        message={dialog.message}
        type={dialog.type}
        title={dialog.title}
        onClose={() => setDialog({ ...dialog, open: false })}
        buttonText="OK"
      />
    </Box>
  );
};

export default ReferralPage;